import { STORAGE_KEYS, LEGACY_AUTH_STORAGE_KEYS } from '@/constants/storageKeys';
import authActions from '@redux/auth/actions';

let storeRef = null;
let persistorRef = null;
let resetPromise = null;
let sessionResetting = false;
const requestControllers = new Set();

const SESSION_RESET_EVENT_TYPES = new Set([
  authActions.SESSION_RESET,
  authActions.SESSION_RESET_REMOTE,
  'LOGOUT',
]);

const safeLocalStorage = () => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch (error) {
    return null;
  }
};

const getCurrentUserId = () => {
  return storeRef?.getState?.()?.Auth?.userId || null;
};

const removeKey = (storage, key) => {
  try {
    storage.removeItem(key);
  } catch (error) {
    // Ignore storage failures so reset can continue.
  }
};

export const sessionManager = {
  configure({ store, persistor }) {
    storeRef = store;
    persistorRef = persistor;
  },

  getSessionSnapshot() {
    const auth = storeRef?.getState?.()?.Auth || {};
    return {
      access: auth.access || null,
      refresh: auth.refresh || null,
      accessTokenExpiration: auth.accessTokenExpiration || null,
      refreshTokenExpiration: auth.refreshTokenExpiration || null,
      userId: auth.userId || null,
      sessionResetting: sessionResetting || Boolean(auth.sessionResetting),
      sessionEpoch: auth.sessionEpoch || 0,
    };
  },

  hasSession() {
    const snapshot = this.getSessionSnapshot();
    const storage = safeLocalStorage();
    return Boolean(
      snapshot.access ||
        snapshot.refresh ||
        storage?.getItem(STORAGE_KEYS.PERSIST_AUTH) ||
        storage?.getItem(STORAGE_KEYS.PERSIST_ROOT) ||
        LEGACY_AUTH_STORAGE_KEYS.some((key) => storage?.getItem(key))
    );
  },

  isResetting() {
    return sessionResetting || Boolean(storeRef?.getState?.()?.Auth?.sessionResetting);
  },

  registerRequestController(controller) {
    requestControllers.add(controller);
    return () => requestControllers.delete(controller);
  },

  cancelRequests() {
    requestControllers.forEach((controller) => {
      try {
        controller.abort();
      } catch (error) {
        // Ignore already aborted controllers.
      }
    });
    requestControllers.clear();
  },

  clearScopedCaches(userId = getCurrentUserId()) {
    const localStorage = safeLocalStorage();
    if (localStorage) {
      if (userId) {
        removeKey(localStorage, STORAGE_KEYS.portfolioSelectedFacilities(userId));
      }
      removeKey(localStorage, STORAGE_KEYS.LEGACY_PORTFOLIO_SELECTED_FACILITIES);
    }

    const sessionStorage = (() => {
      if (typeof window === 'undefined') return null;
      try { return window.sessionStorage; } catch (_) { return null; }
    })();
    if (sessionStorage) {
      removeKey(sessionStorage, 'persist:view-ctx');
      removeKey(sessionStorage, 'persist:company-ctx');
      removeKey(sessionStorage, 'persist:facility-ctx');
      if (userId) {
        removeKey(sessionStorage, STORAGE_KEYS.switchViewSelection(userId));
      }
    }
  },

  clearAuthStorage({ includePersist = true, userId = getCurrentUserId() } = {}) {
    const storage = safeLocalStorage();
    if (!storage) return;

    LEGACY_AUTH_STORAGE_KEYS.forEach((key) => removeKey(storage, key));
    this.clearScopedCaches(userId);

    if (includePersist) {
      removeKey(storage, STORAGE_KEYS.PERSIST_AUTH);
      removeKey(storage, STORAGE_KEYS.PERSIST_ROOT);
    }
  },

  broadcast(type = authActions.SESSION_RESET) {
    const storage = safeLocalStorage();
    if (!storage || !SESSION_RESET_EVENT_TYPES.has(type)) return;

    try {
      storage.setItem(
        STORAGE_KEYS.AUTH_EVENT,
        JSON.stringify({
          type,
          at: Date.now(),
        })
      );
    } catch (error) {
      // Broadcast is best-effort.
    }
  },

  async reset(options = {}) {
    const {
      reason = 'manual',
      redirect = true,
      broadcast = true,
      remote = false,
      target = '/login',
    } = options;

    if (resetPromise) {
      return resetPromise;
    }

    resetPromise = (async () => {
      const previousUserId = getCurrentUserId();
      sessionResetting = true;

      try {
        if (storeRef && typeof storeRef.dispatch === 'function') {
          storeRef.dispatch({
            type: authActions.SESSION_RESET_REQUEST,
            payload: { reason },
          });
        }

        this.cancelRequests();
        this.clearScopedCaches(previousUserId);

        if (persistorRef) {
          try {
            persistorRef.pause();
          } catch (error) {
            // Older redux-persist implementations can safely skip pause failures.
          }
        }

        if (storeRef && typeof storeRef.dispatch === 'function') {
          storeRef.dispatch({
            type: remote ? authActions.SESSION_RESET_REMOTE : authActions.SESSION_RESET,
            payload: { reason },
          });
        }

        if (persistorRef) {
          try {
            await persistorRef.flush();
          } catch (error) {
            // Continue local reset even if flushing fails.
          }
          try {
            await persistorRef.purge();
          } catch (error) {
            // Continue local reset even if purge fails.
          }
        }

        this.clearAuthStorage({ includePersist: true, userId: previousUserId });

        if (storeRef && typeof storeRef.dispatch === 'function') {
          storeRef.dispatch({
            type: authActions.SESSION_RESET_SUCCESS,
            payload: { reason },
          });
        }

        if (broadcast) {
          this.broadcast(authActions.SESSION_RESET);
        }

        if (redirect && typeof window !== 'undefined') {
          window.location.replace(target);
        }
      } finally {
        sessionResetting = false;
        resetPromise = null;
        try {
          if (persistorRef && typeof persistorRef.persist === 'function') {
            persistorRef.persist();
          }
        } catch (error) {
          // Ignore persist resume failures.
        }
      }
    })();

    return resetPromise;
  },
};

export const setupSessionBroadcastListener = () => {
  if (typeof window === 'undefined') return;

  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEYS.AUTH_EVENT || !event.newValue) return;

    try {
      const payload = JSON.parse(event.newValue);
      if (SESSION_RESET_EVENT_TYPES.has(payload?.type)) {
        sessionManager.reset({
          reason: 'remote',
          redirect: true,
          broadcast: false,
          remote: true,
        });
      }
    } catch (error) {
      // Ignore malformed cross-tab events.
    }
  });
};

export default sessionManager;
